import type { IconName } from '@/components/Icon';
import type { AlertGroupBy, NotificationCondition, NotificationRule } from '@/types/sturm';

export interface ConditionDefinition {
  id: NotificationCondition;
  name: string;
  sub: string;
  icon: IconName;
}

export const CONDITIONS: ConditionDefinition[] = [
  { id: 'run-failed', name: 'Run failed', sub: 'After all retries are exhausted', icon: 'alert' },
  { id: 'step-retrying', name: 'Step retried', sub: 'More than N attempts on one step', icon: 'refresh' },
  { id: 'run-stuck', name: 'Run stuck', sub: 'Running longer than a threshold', icon: 'clock' },
  { id: 'failure-rate', name: 'Failure rate', sub: 'Above X% over a window', icon: 'flag' },
  { id: 'schedule-missed', name: 'Schedule missed', sub: 'No run started when expected', icon: 'calendar' },
  { id: 'queue-backlog', name: 'Queue backlog', sub: 'Tasks waiting above a threshold', icon: 'layers' },
  { id: 'terminated', name: 'Run terminated', sub: 'Cancelled or terminated by a human', icon: 'stop' },
];

export const SEVERITIES = [
  { id: 'critical', label: 'Critical', sub: 'Pages the on-call rotation' },
  { id: 'warning', label: 'Warning', sub: 'Posts to chat, no page' },
  { id: 'info', label: 'Info', sub: 'Recorded, no notification noise' },
] as const;

export const GROUP_BY_OPTIONS: { value: AlertGroupBy; label: string }[] = [
  { value: 'run', label: 'Each run separately' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'workflow + step', label: 'Workflow + step' },
  { value: 'queue', label: 'Task queue' },
  { value: 'error', label: 'Error type' },
];

export const THROTTLE_OPTIONS = [
  { value: 'none', label: 'Send every time' },
  { value: '5m', label: 'At most every 5 minutes' },
  { value: '15m', label: 'At most every 15 minutes' },
  { value: '30m', label: 'At most every 30 minutes' },
  { value: '1h', label: 'At most every hour' },
];

export function conditionSummary(rule: Pick<NotificationRule, 'condition' | 'parameters'>): string {
  const p = rule.parameters ?? {};

  switch (rule.condition) {
    case 'run-failed':
      return p.steps?.length ? `Run fails at ${p.steps.join(', ')}` : 'Run fails after all retries';
    case 'step-retrying':
      return `A step retries more than ${p.attempts ?? 3} times`;
    case 'run-stuck':
      return `A run is still running after ${p.runningLongerThan ?? '1h'}`;
    case 'failure-rate':
      return `Failure rate above ${p.failurePercent ?? 10}% over ${p.window ?? '30m'}`;
    case 'schedule-missed':
      return `No run started within ${p.grace ?? '10m'} of schedule`;
    case 'queue-backlog':
      return `Queue depth above ${p.queuedTasks ?? 200} tasks for ${p.sustainedFor ?? '10m'}`;
    case 'terminated':
      return 'A run is cancelled or terminated by a person';
    default:
      return rule.condition;
  }
}

export function scopeLabel(scope: string[], names: Record<string, string>): string {
  if (scope[0] === '*') {
    return 'All workflows';
  }

  return scope.map((slug) => names[slug] ?? slug).join(', ');
}
